import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CloudSyncOutlined,
  CloudUploadOutlined,
  DownloadOutlined,
  FactCheckOutlined,
  OpenInNew,
  PlayArrowOutlined,
  SyncAltOutlined,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  Discrepancy,
  DiscrepancyStatus,
  ResolveItem,
  ResolveResult,
  runCRNReconciliation,
  runReconciliationResolve,
  selectReconciliationError,
  selectReconciliationReport,
  selectReconciliationResolveError,
  selectReconciliationResolveReport,
  selectReconciliationResolving,
  selectReconciliationStatus,
} from '../features/reconciliation/reconciliation';
import { chargebeeCustomerUrl } from '../accounts/Accounts';

const POPULATE_HINT = "Write the DynamoDB CRN into HubSpot's "
  + 'customer_reference_number for contacts that are missing it';
const APPLY_HINT = 'Push the HubSpot CRN into Chargebee and DynamoDB for '
  + 'mismatched records, unless that CRN is already in use';
const FORCE_HINT = 'Overwrite HubSpot with the DynamoDB CRN (the source of truth) for every '
  + 'discrepancy, including mismatches. Chargebee and DynamoDB are left unchanged';

const resolveKey = (email: string, dynamoCrn: string) => `${email}|${dynamoCrn}`;

// Build a CSV of the write-back results (email + authoritative CRN + outcome) and
// trigger a browser download for customer comms.
const downloadCsv = (rows: ResolveResult[]) => {
  const esc = (v: string) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const header = ['email', 'crn', 'applied', 'reason'];
  const body = rows.map((r) => [
    r.email, r.dynamo_crn, r.applied ? 'yes' : 'no', r.reason ?? '',
  ].map(esc).join(','));
  const blob = new Blob([[header.join(','), ...body].join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'crn-reconciliation.csv';
  a.click();
  URL.revokeObjectURL(url);
};

const toItem = (d: Discrepancy): ResolveItem => ({
  email: d.email,
  cb_customer_id: d.cb_customer_id,
  dynamo_crn: d.dynamo_crn,
  hubspot_crn: d.hubspot_crn,
});

const resultChip = (res: ResolveResult) => {
  if (res.applied) {
    return <Chip size="small" color="success" label={`Applied${res.crn ? ` → ${res.crn}` : ''}`} />;
  }
  const reason = res.reason ?? 'skipped';
  const failed = reason.startsWith('error:');
  return (
    <Tooltip title={reason}>
      <Chip
        size="small"
        variant="outlined"
        color={failed ? 'error' : 'default'}
        label={failed ? 'Failed' : 'Skipped'}
      />
    </Tooltip>
  );
};

const STAT = (
  { label, value, highlight = undefined }:
  { label: string; value: number; highlight?: 'error' | 'success' | 'warning' },
) => (
  <Box sx={{ textAlign: 'center', minWidth: 90 }}>
    <Typography
      variant="h6"
      lineHeight={1}
      color={highlight ? `${highlight}.main` : 'text.primary'}
    >
      {value.toLocaleString()}
    </Typography>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Box>
);

const statusChip = (status: DiscrepancyStatus) => {
  switch (status) {
    case 'mismatch':
      return <Chip size="small" color="error" label="Mismatch" />;
    case 'missing_in_hubspot':
      return <Chip size="small" color="warning" label="Missing in HubSpot" />;
    case 'contact_not_found':
      return <Chip size="small" variant="outlined" label="No HubSpot contact" />;
    default:
      return <Chip size="small" label={status} />;
  }
};

const CRNReconciliationDemo = () => {
  const dispatch = useAppDispatch();
  const report = useAppSelector(selectReconciliationReport);
  const status = useAppSelector(selectReconciliationStatus);
  const error = useAppSelector(selectReconciliationError);
  const resolving = useAppSelector(selectReconciliationResolving);
  const resolveReport = useAppSelector(selectReconciliationResolveReport);
  const resolveError = useAppSelector(selectReconciliationResolveError);

  const [limit, setLimit] = useState(2000);
  const [includeFreed, setIncludeFreed] = useState(false);

  const loading = status === 'loading';

  const run = () => {
    dispatch(runCRNReconciliation({ limit, includeFreed }));
  };

  const discrepancies = report?.discrepancies ?? [];
  const missingItems = discrepancies
    .filter((d) => d.status === 'missing_in_hubspot')
    .map(toItem);
  const mismatchItems = discrepancies
    .filter((d) => d.status === 'mismatch')
    .map(toItem);
  // Force-push targets every discrepancy that has a HubSpot contact to write to
  // (mismatch + missing); contact_not_found has nothing to update.
  const forceItems = discrepancies
    .filter((d) => d.status === 'mismatch' || d.status === 'missing_in_hubspot')
    .map(toItem);

  const populateHubspot = () => {
    dispatch(runReconciliationResolve({ action: 'populate-hubspot', items: missingItems }));
  };
  const applyHubspot = () => {
    dispatch(runReconciliationResolve({ action: 'apply-hubspot', items: mismatchItems }));
  };
  const forceHubspot = () => {
    dispatch(runReconciliationResolve({ action: 'force-hubspot', items: forceItems }));
  };

  // Index the last write-back outcome by email|dynamo_crn so each row can show
  // whether it was applied, skipped, or failed.
  const resultsByRow = new Map<string, ResolveResult>();
  (resolveReport?.results ?? []).forEach((res) => {
    resultsByRow.set(resolveKey(res.email, res.dynamo_crn), res);
  });

  return (
    <Box sx={{ mr: 15, ml: 15, mt: 10 }}>
      <Paper elevation={0} sx={{ p: 2 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, mb: 1,
        }}
        >
          <FactCheckOutlined />
          <Typography variant="h6">CRN ⇄ HubSpot Reconciliation</Typography>
          <Chip size="small" variant="outlined" label="Demo" />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Scans DynamoDB accounts and compares each BPAY CRN (the account sort key)
          against the matching HubSpot contact&apos;s
          {' '}
          <code>customer_reference_number</code>
          . Contacts are matched by email.
        </Typography>

        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1,
        }}
        >
          <TextField
            size="small"
            type="number"
            label="Scan limit"
            value={limit}
            onChange={(e) => setLimit(Math.max(1, Number(e.target.value) || 0))}
            sx={{ width: 140 }}
          />
          <FormControlLabel
            control={(
              <Switch
                checked={includeFreed}
                onChange={(e) => setIncludeFreed(e.target.checked)}
              />
            )}
            label="Include freed accounts"
          />
          <Button
            variant="contained"
            onClick={run}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <PlayArrowOutlined />}
          >
            {loading ? 'Running…' : 'Run reconciliation'}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        {report && !loading && (
          <>
            <Divider sx={{ my: 2 }} />

            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2,
            }}
            >
              <STAT label="scanned" value={report.scanned} />
              <Divider orientation="vertical" flexItem />
              <STAT label="compared" value={report.compared} />
              <Divider orientation="vertical" flexItem />
              <STAT label="matched" value={report.matched} highlight="success" />
              <Divider orientation="vertical" flexItem />
              <STAT label="mismatched" value={report.mismatched} highlight={report.mismatched > 0 ? 'error' : undefined} />
              <Divider orientation="vertical" flexItem />
              <STAT label="missing in HubSpot" value={report.missing_in_hubspot} highlight={report.missing_in_hubspot > 0 ? 'warning' : undefined} />
              <Divider orientation="vertical" flexItem />
              <STAT label="no contact" value={report.contact_not_found} />
              <Divider orientation="vertical" flexItem />
              <STAT label="skipped" value={report.skipped_no_email} />
            </Box>

            {report.truncated && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Scan stopped at the limit of
                {' '}
                {limit}
                {' '}
                accounts — increase the limit to cover the full table.
              </Alert>
            )}

            {(missingItems.length > 0 || mismatchItems.length > 0) && (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2,
              }}
              >
                <Tooltip title={POPULATE_HINT}>
                  <span>
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={populateHubspot}
                      disabled={missingItems.length === 0 || resolving !== null}
                      startIcon={resolving === 'populate-hubspot'
                        ? <CircularProgress size={16} /> : <CloudUploadOutlined />}
                    >
                      {`Populate ${missingItems.length} missing in HubSpot`}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={APPLY_HINT}>
                  <span>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={applyHubspot}
                      disabled={mismatchItems.length === 0 || resolving !== null}
                      startIcon={resolving === 'apply-hubspot'
                        ? <CircularProgress size={16} /> : <SyncAltOutlined />}
                    >
                      {`Apply ${mismatchItems.length} mismatched from HubSpot`}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={FORCE_HINT}>
                  <span>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={forceHubspot}
                      disabled={forceItems.length === 0 || resolving !== null}
                      startIcon={resolving === 'force-hubspot'
                        ? <CircularProgress size={16} /> : <CloudSyncOutlined />}
                    >
                      {`Force-push ${forceItems.length} Dynamo → HubSpot`}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            )}

            {resolveError && <Alert severity="error" sx={{ mb: 2 }}>{resolveError}</Alert>}

            {resolveReport && (
              <Box sx={{ mb: 2 }}>
                <Alert
                  severity={resolveReport.failed > 0 ? 'warning' : 'success'}
                  action={(
                    <Button
                      color="inherit"
                      size="small"
                      startIcon={<DownloadOutlined />}
                      onClick={() => downloadCsv(resolveReport.results)}
                      disabled={resolveReport.results.length === 0}
                    >
                      Download CSV
                    </Button>
                  )}
                >
                  {`Applied ${resolveReport.applied}, skipped ${resolveReport.skipped}, `}
                  {`failed ${resolveReport.failed}. `}
                  Download the CSV for customer comms, then re-run the reconciliation to refresh.
                </Alert>
              </Box>
            )}

            {report.discrepancies.length === 0 ? (
              <Alert severity="success">No discrepancies found. All compared CRNs match HubSpot.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Dynamo CRN</TableCell>
                      <TableCell>HubSpot CRN</TableCell>
                      <TableCell>Chargebee</TableCell>
                      <TableCell>Result</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.discrepancies.map((d: Discrepancy) => (
                      <TableRow key={`${d.email}|${d.dynamo_crn}`}>
                        <TableCell>{statusChip(d.status)}</TableCell>
                        <TableCell>{d.email}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{d.dynamo_crn || '—'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{d.hubspot_crn || '—'}</TableCell>
                        <TableCell>
                          {d.cb_customer_id ? (
                            <Tooltip title="Open customer in Chargebee">
                              <Box
                                component="a"
                                href={chargebeeCustomerUrl(d.cb_customer_id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  color: 'primary.main',
                                  textDecoration: 'none',
                                }}
                              >
                                {d.cb_customer_id}
                                <OpenInNew sx={{ fontSize: 12 }} />
                              </Box>
                            </Tooltip>
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          {resultsByRow.has(resolveKey(d.email, d.dynamo_crn))
                            ? resultChip(resultsByRow.get(resolveKey(d.email, d.dynamo_crn))!)
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default CRNReconciliationDemo;
